import { forwardRef } from "react";

import { Icon, type IconProps, type IconRef } from "./icon";
import {
  stepsContentVariants,
  stepsIndicatorVariants,
  stepsListItemVariants,
  stepsListVariants,
  stepsRootVariants,
  stepsTrackVariants,
  type StepsContentVariants,
  type StepsIndicatorVariants,
  type StepsListItemVariants,
  type StepsListVariants,
  type StepsRootVariants,
  type StepsTrackVariants,
} from "./steps.styles";
import { toStepsIconName } from "./steps.utils";

// -----------------------------------------------------------------------------

export interface StepsItem {
  id: string;
  children: React.ReactElement<any> | string;
}

export type StepsItems = [StepsItem, StepsItem, ...StepsItem[]];

// -----------------------------------------------------------------------------

export type StepsRootRef = React.ComponentRef<"nav">;

export interface StepsRootProps
  extends React.ComponentPropsWithoutRef<"nav">, StepsRootVariants {}

const StepsRoot = forwardRef<StepsRootRef, StepsRootProps>(
  ({ className, ...props }, ref) => {
    return (
      <nav
        aria-label="Progress"
        {...props}
        className={stepsRootVariants({ className })}
        ref={ref}
      />
    );
  },
);

StepsRoot.displayName = "StepsRoot";

export { StepsRoot };

// -----------------------------------------------------------------------------

export type StepsListRef = React.ComponentRef<"ol">;

export interface StepsListProps
  extends React.ComponentPropsWithoutRef<"ol">, StepsListVariants {}

const StepsList = forwardRef<StepsListRef, StepsListProps>(
  ({ className, ...props }, ref) => {
    return (
      <ol
        {...props}
        // Unstyled lists are not announced as lists by VoiceOver on Mac,
        // so we need to keep the role=list for accessibility.
        // oxlint-disable-next-line jsx-a11y/no-redundant-roles
        role="list"
        className={stepsListVariants({ className })}
        ref={ref}
      />
    );
  },
);

StepsList.displayName = "StepsList";

export { StepsList };

// -----------------------------------------------------------------------------

export type StepsListItemRef = React.ComponentRef<"li">;

export interface StepsListItemProps
  extends React.ComponentPropsWithoutRef<"li">, StepsListItemVariants {}

const StepsListItem = forwardRef<StepsListItemRef, StepsListItemProps>(
  ({ className, ...props }, ref) => {
    return (
      <li
        {...props}
        data-type="item"
        className={stepsListItemVariants({ className })}
        ref={ref}
      />
    );
  },
);

StepsListItem.displayName = "StepsListItem";

export { StepsListItem };

// -----------------------------------------------------------------------------

export type StepsTrackRef = React.ComponentRef<"span">;

export interface StepsTrackProps
  extends
    Omit<React.ComponentPropsWithoutRef<"span">, "aria-hidden" | "children">,
    StepsTrackVariants {}

const StepsTrack = forwardRef<StepsTrackRef, StepsTrackProps>(
  ({ className, edge, variant, ...props }, ref) => {
    return (
      <span
        {...props}
        aria-hidden
        data-type="track"
        className={stepsTrackVariants({ className, edge, variant })}
        ref={ref}
      >
        <span data-type="track-progress" />
      </span>
    );
  },
);

StepsTrack.displayName = "StepsTrack";

export { StepsTrack };

// -----------------------------------------------------------------------------

export type StepsIndicatorRef = IconRef;

export interface StepsIndicatorProps
  extends
    Omit<IconProps, "data-type" | "name" | "palette">,
    StepsIndicatorVariants {}

const StepsIndicator = forwardRef<StepsIndicatorRef, StepsIndicatorProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <Icon
        {...props}
        name={toStepsIconName(variant)}
        data-type="indicator"
        palette="unset"
        className={stepsIndicatorVariants({ className, variant })}
        ref={ref}
      />
    );
  },
);

StepsIndicator.displayName = "StepsIndicator";

export { StepsIndicator };

// -----------------------------------------------------------------------------

export type StepsContentRef = React.ComponentRef<"span">;

export interface StepsContentProps
  extends React.ComponentPropsWithoutRef<"span">, StepsContentVariants {}

const StepsContent = forwardRef<StepsContentRef, StepsContentProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <span
        {...props}
        className={stepsContentVariants({ className, variant })}
        ref={ref}
      />
    );
  },
);

StepsContent.displayName = "StepsContent";

export { StepsContent };
